/**
 * Work Orders Bulk-Action API
 *
 * Thin HTTP wrappers around the backend's directional outbound endpoints. The
 * backend owns the event transition for each action (accepted/rejected/more
 * info requested) and returns a per-job `results` array so we can surface
 * partial-success outcomes in the UI.
 *
 * All three endpoints are mounted under `/works-lite/work-orders/outbound/*`
 * (per `workOrderDirectionalContract`) and always respond 200 OK regardless of
 * per-job failures — callers must inspect `results`.
 */

import { apiClient } from '@/lib/api';
import type {
  OutboundAcceptedBody,
  OutboundAcceptedResponse,
  OutboundAllocatedBody,
  OutboundAllocatedResponse,
  OutboundCancelResponse,
  OutboundDurationChallengeBody,
  OutboundDurationChallengeResponse,
  OutboundEngineerEnRouteBody,
  OutboundEngineerEnRouteResponse,
  OutboundEngineerOnSiteBody,
  OutboundEngineerOnSiteResponse,
  OutboundEngineeringCompleteBody,
  OutboundEngineeringCompleteResponse,
  OutboundMoreInfoReceivedBody,
  OutboundMoreInfoReceivedResponse,
  OutboundMoreInfoRequestedBody,
  OutboundMoreInfoRequestedResponse,
  OutboundRejectedBody,
  OutboundRejectedResponse,
  OutboundScheduledBody,
  OutboundScheduledResponse,
  OutboundUnscheduledBody,
  OutboundUnscheduledResponse,
  OutboundValidationRequestedBody,
  OutboundValidationRequestedResponse,
} from '@skewb-works/contracts';

/**
 * Unified response shape across all three bulk actions. The backend's
 * per-endpoint response types all share the same `{ results: [{id, success,
 * message}] }` shell, so callers can treat them interchangeably.
 */
export type BulkActionResponse =
  | OutboundAcceptedResponse
  | OutboundRejectedResponse
  | OutboundMoreInfoRequestedResponse;

export async function acceptWorkOrders(ids: number[]): Promise<OutboundAcceptedResponse> {
  const body: OutboundAcceptedBody = { ids };
  return apiClient.post<OutboundAcceptedResponse>(
    '/works-lite/work-orders/outbound/accepted',
    body,
  );
}

export async function rejectWorkOrders(
  rejections: OutboundRejectedBody['rejections'],
): Promise<OutboundRejectedResponse> {
  const body: OutboundRejectedBody = { rejections };
  return apiClient.post<OutboundRejectedResponse>(
    '/works-lite/work-orders/outbound/rejected',
    body,
  );
}

export async function requestMoreInfoWorkOrders(
  requests: OutboundMoreInfoRequestedBody['requests'],
): Promise<OutboundMoreInfoRequestedResponse> {
  const body: OutboundMoreInfoRequestedBody = { requests };
  return apiClient.post<OutboundMoreInfoRequestedResponse>(
    '/works-lite/work-orders/outbound/more-info-requested',
    body,
  );
}

/**
 * Reply to an inbound more-info request from a subcontractor.
 * Sends a work_order.more_info_received event via the outbound endpoint.
 * Accepts the same per-job array shape as requestMoreInfoWorkOrders;
 * reply is always single-job so the first item is used to build the payload.
 */
export async function replyToMoreInfoWorkOrders(
  requests: OutboundMoreInfoRequestedBody['requests'],
): Promise<OutboundMoreInfoReceivedResponse> {
  const body: OutboundMoreInfoReceivedBody = { jobWorkOrder: requests[0] };
  return apiClient.post<OutboundMoreInfoReceivedResponse>(
    '/works-lite/work-orders/outbound/more-info-received',
    body,
  );
}

export async function scheduleWorkOrder(id: number): Promise<OutboundScheduledResponse> {
  const body: OutboundScheduledBody = { id };
  return apiClient.post<OutboundScheduledResponse>(
    '/works-lite/work-orders/outbound/scheduled',
    body,
  );
}

export async function allocateWorkOrder(
  data: OutboundAllocatedBody,
): Promise<OutboundAllocatedResponse> {
  return apiClient.post<OutboundAllocatedResponse>(
    '/works-lite/work-orders/outbound/allocated',
    data,
  );
}

export async function unscheduleWorkOrder(id: number): Promise<OutboundUnscheduledResponse> {
  const body: OutboundUnscheduledBody = { id };
  return apiClient.post<OutboundUnscheduledResponse>(
    '/works-lite/work-orders/outbound/unscheduled',
    body,
  );
}

export async function engineerEnRoute(id: number): Promise<OutboundEngineerEnRouteResponse> {
  const body: OutboundEngineerEnRouteBody = { id };
  return apiClient.post<OutboundEngineerEnRouteResponse>(
    '/works-lite/work-orders/outbound/engineer-en-route',
    body,
  );
}

export async function engineerOnSite(id: number): Promise<OutboundEngineerOnSiteResponse> {
  const body: OutboundEngineerOnSiteBody = { id };
  return apiClient.post<OutboundEngineerOnSiteResponse>(
    '/works-lite/work-orders/outbound/engineer-on-site',
    body,
  );
}

export async function engineeringComplete(
  data: OutboundEngineeringCompleteBody,
): Promise<OutboundEngineeringCompleteResponse> {
  return apiClient.post<OutboundEngineeringCompleteResponse>(
    '/works-lite/work-orders/outbound/engineering-complete',
    data,
  );
}

export async function validationRequested(
  data: OutboundValidationRequestedBody,
): Promise<OutboundValidationRequestedResponse> {
  return apiClient.post<OutboundValidationRequestedResponse>(
    '/works-lite/work-orders/outbound/validation-requested',
    data,
  );
}

export async function durationChallengeWorkOrder(
  body: OutboundDurationChallengeBody,
): Promise<OutboundDurationChallengeResponse> {
  return apiClient.post<OutboundDurationChallengeResponse>(
    '/works-lite/work-orders/outbound/duration-challenge',
    body,
  );
}

export interface AbortWorkOrderNote {
  id: string;
  note: string;
  createdBy: string;
  createdDate: string;
  category?: string;
}

export interface AbortWorkOrderBody {
  id: number;
  notes: AbortWorkOrderNote[];
}

export async function abortWorkOrder(body: AbortWorkOrderBody): Promise<void> {
  return apiClient.post<void>('/works-lite/work-orders/outbound/aborted', body);
}

export async function cancelFromAbort(id: number): Promise<void> {
  return apiClient.post<void>('/works-lite/work-orders/outbound/cancel-from-abort', { id });
}

export async function replanFromAbort(id: number): Promise<void> {
  return apiClient.post<void>('/works-lite/work-orders/outbound/replan-from-abort', { id });
}

export async function cancelWorkOrder(id: number): Promise<OutboundCancelResponse> {
  return apiClient.post<OutboundCancelResponse>('/works-lite/work-orders/outbound/cancel', { id });
}
